import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { emailChanged, passwordChanged, Login, GoogleLogin, clearLoginError } from "../../../src/actions/auth_actions.js";
import { useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { Toast } from "bootstrap";
import { AppSettings } from "../../config/app-settings.js";
import styles from "./sentinelLogin.module.css";

// Sentinel-style login (visual match of sentinel.barsys.com/login):
// dark starfield, Barsys mark, Google-first sign-in with the password form
// tucked behind a small link for accounts outside Google Workspace.

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const PagesLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { setAppHeaderNone, setAppSidebarNone, setAppContentClass } = useContext(AppSettings);

  // Redux state for authentication
  const email = useSelector((state) => state.auth.email);
  const password = useSelector((state) => state.auth.password);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const loginError = useSelector((state) => state.auth.logininfo?.error || null);

  // Local state
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [showPwForm, setShowPwForm] = useState(false);

  // Hide the header and sidebar on mount, restore on unmount.
  useEffect(() => {
    setAppHeaderNone(true);
    setAppSidebarNone(true);
    setAppContentClass("p-0");
    return () => {
      setAppHeaderNone(false);
      setAppSidebarNone(false);
      setAppContentClass("");
    };
  }, [setAppHeaderNone, setAppSidebarNone, setAppContentClass]);

  // Load the display font once
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  }, []);

  // ---- Starfield background ----
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const count = Math.min(180, Math.floor((canvas.width * canvas.height) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.3 + 0.3,
        base: Math.random() * 0.35 + 0.1,
        phase: Math.random() * Math.PI * 2,
        speed: Math.random() * 0.0012 + 0.0004,
        drift: Math.random() * 0.05 + 0.01,
      }));
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        const tw = s.base + Math.sin(t * s.speed + s.phase) * s.base;
        ctx.globalAlpha = Math.max(0.04, tw);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        s.y -= s.drift;
        if (s.y < -2) {
          s.y = canvas.height + 2;
          s.x = Math.random() * canvas.width;
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ---- Google Workspace SSO (barsys domains only) ----
  const googleBtnRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(null); // null = loading
  useEffect(() => {
    let cancelled = false;
    const apiUrl = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const init = (clientId) => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        hd: "barsys.io",
        callback: (resp) => {
          if (resp?.credential) dispatch(GoogleLogin(resp.credential));
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        shape: "pill",
        width: 288,
        text: "continue_with",
        logo_alignment: "left",
      });
      setGoogleReady(true);
    };
    fetch(`${apiUrl}/api/analytics/login/google/config`)
      .then((r) => r.json())
      .then((cfg) => {
        if (cancelled) return;
        if (!cfg?.enabled || !cfg.clientId) {
          setGoogleEnabled(false);
          return;
        }
        setGoogleEnabled(true);
        if (window.google?.accounts?.id) return init(cfg.clientId);
        const scr = document.createElement("script");
        scr.src = "https://accounts.google.com/gsi/client";
        scr.async = true;
        scr.onload = () => init(cfg.clientId);
        document.head.appendChild(scr);
      })
      .catch(() => {
        if (!cancelled) setGoogleEnabled(false);
      });
    return () => { cancelled = true; };
  }, [dispatch]);

  const handleLogin = (event) => {
    event.preventDefault();
    if (!email || !password) {
      setToastMsg("Email and password are required!");
      const toastElem = document.getElementById("toast-2");
      if (toastElem) new Toast(toastElem).show();
      return;
    }
    dispatch(Login(email, password));
  };

  // After auth, go to root; RootRedirect will take you to the first allowed page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Show error toast if a login error exists.
  useEffect(() => {
    if (loginError) {
      setToastMsg(loginError);
      const toastElem = document.getElementById("toast-2");
      if (toastElem) new Toast(toastElem).show();
      dispatch(clearLoginError());
    }
  }, [loginError, dispatch]);

  // "Forgot password?" toast trigger.
  useEffect(() => {
    const toastTrigger = document.getElementById("showToast1");
    const toastElem = document.getElementById("toast-1");
    if (toastTrigger && toastElem) {
      const handler = (e) => { e.preventDefault(); new Toast(toastElem).show(); };
      toastTrigger.addEventListener("click", handler);
      return () => toastTrigger.removeEventListener("click", handler);
    }
  });

  // If SSO is disabled/unreachable, the password form is the only way in.
  const passwordFormOpen = showPwForm || googleEnabled === false;

  return (
    <div className={styles.wrapper}>
      <canvas ref={canvasRef} className={styles.stars} />

      <div className={styles.center}>
        <div className={styles.logoTile}>
          <img src="/Barsys_favicon.png" alt="Barsys" />
        </div>
        <div className={styles.brand}>BARSYS</div>
        <div className={styles.brandSub}>Analytics Dashboard</div>

        <div className={styles.card}>
          <div className={styles.welcome}>WELCOME</div>
          <div className={styles.welcomeSub}>
            Sign in with your Barsys Google account to continue
          </div>

          {/* Google-first sign-in: Google's real button (pill shape) must stay
              visible — GIS ignores clicks on hidden/overlaid buttons. */}
          {googleEnabled !== false && (
            <div className={styles.googleWrap}>
              <div ref={googleBtnRef} className={styles.gisHost}></div>
            </div>
          )}

          <div className={styles.dividerRow}>
            <hr />
            <span className={styles.dividerLabel}>BARSYS ONLY</span>
            <hr />
          </div>

          <div className={styles.chips}>
            <span className={styles.chip}>@barsys.io</span>
            <span className={styles.chip}>@barsys.com</span>
          </div>

          {googleEnabled !== false && (
            <button
              type="button"
              className={styles.pwToggle}
              onClick={() => setShowPwForm((v) => !v)}
            >
              {passwordFormOpen ? "Hide password sign-in" : "Sign in with password instead"}
            </button>
          )}

          {passwordFormOpen && (
            <Form className={styles.pwForm} onSubmit={handleLogin}>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => dispatch(emailChanged(e.target.value))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="formBasicPassword">
                <Form.Label>Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => dispatch(passwordChanged(e.target.value))}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    className={styles.showBtn}
                    onClick={() => setPasswordVisible((prev) => !prev)}
                    type="button"
                  >
                    {passwordVisible ? "Hide" : "Show"}
                  </Button>
                </div>
              </Form.Group>

              <div className="text-end mb-3">
                <a href="#!" id="showToast1" className={styles.forgot}>
                  Forgot password?
                </a>
              </div>

              <Button type="submit" className={styles.loginBtn} id="loginbtn" variant="dark">
                Login
              </Button>
            </Form>
          )}
        </div>

        <div className={styles.tagline}>Precision in every decision</div>
      </div>

      {/* Toasts */}
      <div
        className="toasts-container"
        style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1050,
        }}
      >
        <div className="toast fade hide" data-autohide="false" id="toast-1">
          <div className="toast-header">
            <i className="far fa-bell text-muted me-2"></i>
            <strong className="me-auto">ALERT</strong>
            <button type="button" className="btn-close" data-bs-dismiss="toast"></button>
          </div>
          <div className="toast-body">Please contact Admin.</div>
        </div>
        <div className="toast fade hide" data-autohide="false" id="toast-2">
          <div className="toast-header">
            <i className="far fa-bell text-muted me-2"></i>
            <strong className="me-auto">ALERT</strong>
            <button type="button" className="btn-close" data-bs-dismiss="toast"></button>
          </div>
          <div className="toast-body">{toastMsg}</div>
        </div>
      </div>
    </div>
  );
};

export default PagesLogin;
